<template>
  <div class="container admin-page-py">
    <div class="row">
      <div class="col-12 col-lg-8 offset-lg-2">
        <div class="row mb-5">
          <div class="col-12 text-center">
            <div class="page-title">Task Report</div>
            <div class="page-subtitle">General overview of your task hours summary.</div>
          </div>
        </div>

        <div class="row my-4">
          <div class="col">
            <div class="filter-title">
              <span v-if="startDate === endDate">Today</span>
              <span class="filter-subtitle" v-else>{{ formatDateForDisplay(this.startDate) }}</span>
              <span class="filter-subtitle">- {{ formatDateForDisplay(this.endDate) }}</span>
            </div>
          </div>

          <div class="col col-auto d-flex">
            <button type="button" class="basic-button" @click="showFilterModal = !showFilterModal">
              <FilterIcon/>
              Filters
            </button>
          </div>
        </div>

        <div class="row mb-4" style="margin: 0 -8px;" v-if="loading === false">
          <div class="col-md-4 mb-3 px-2" v-for="stat of stats">
            <stats-box
                :name="stat.name"
                :number="stat.number"
                :unit="stat.unit"
                :link="stat.link"
            ></stats-box>
          </div>
        </div>

        <div class="row mb-4" style="margin: 0 -8px;" v-if="loading === false">
          <div class="col-12 mb-3 px-2">
            <stats-box
                name="Monthly Cost"
                :number="totalCost"
                :unit='"In " + currency'
                description="Monthly payment will be made on every calendar month 7th"
            ></stats-box>
          </div>
        </div>

        <div v-if="loading" class="d-block position-relative">
          <p class="d-flex">
            <span class="spinner-border mr-2" role="status">
              <span class="sr-only">Loading...</span>
            </span>
            Loading report summary...
          </p>
        </div>

        <div class="row mb-5">
          <div class="col-12">
            <div class="row mb-4">
              <div class="col-12">
                <div class="subsection-title">Completed Task</div>
                <div class="subsection-subtitle">Know all the details about the task</div>
              </div>
            </div>

            <div class="row mb-3">
              <div class="col-12">
                <table class="wfh-table">
                  <thead>
                  <tr>
                    <td>Date</td>
                    <td>Description</td>
                    <td>Spent</td>
                    <td>Status</td>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="(task, key) of completedTasks"
                      :key="`completed-${key}`"
                      style="cursor:pointer;"
                      @click="viewTaskDetails(task.detailsPageUrl)"
                      v-if="key < 3"
                  >
                    <td style="white-space: nowrap;">{{ task.date }}</td>
                    <td>{{ task.title }}</td>
                    <td style="white-space: nowrap;">{{ task.time_spent }}</td>
                    <td>
                      <task-status-badge status="completed"></task-status-badge>
                    </td>
                  </tr>

                  <tr v-if="completedTasks.length === 0 && loading === false">
                    <td colspan="4" class="text-center">No Completed tasks</td>
                  </tr>

                  <tr v-if="completedTasks.length === 0 && loading === true">
                    <td colspan="4" class="text-center">
                      <div v-if="loading" class="d-block position-relative">
                        <p class="d-flex">
                          <span class="spinner-border mr-2" role="status">
                            <span class="sr-only">Loading...</span>
                          </span>
                          Loading tasks...
                        </p>
                      </div>
                    </td>
                  </tr>
                  </tbody>

                </table>
              </div>
            </div>

            <div class="row" v-if="completedTasks.length > 3">
              <div class="col-12 col-sm-5 mx-auto">
                <a :href="route('team-members.report.tasks', ['completed'])"
                   class="wfh-btn-color-primary wfh-btn-large btn-block">
                  See All
                </a>
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-12">
            <div>
              <div class="subsection-title">Incomplete Task</div>
              <div class="subsection-subtitle">Know all the details about the task</div>
            </div>

            <div class="row mb-3">
              <div class="col-12">
                <table class="wfh-table">
                  <thead>
                  <tr>
                    <td>Date</td>
                    <td>Description</td>
                    <td>Spent</td>
                    <td>Status</td>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="(task, key) of pendingTasks"
                      :key="`pending-${key}`"
                      style="cursor:pointer;"
                      @click="viewTaskDetails(task.detailsPageUrl)"
                      v-if="key < 3"
                  >
                    <td style="white-space: nowrap;">{{ task.date }}</td>
                    <td>{{ task.title }}</td>
                    <td style="white-space: nowrap;">{{ task.time_spent }}</td>
                    <td>
                      <task-status-badge :status="task.status"></task-status-badge>
                    </td>
                  </tr>

                  <tr v-if="pendingTasks.length === 0 && loading === false">
                    <td colspan="4" class="text-center">No Incomplete tasks</td>
                  </tr>

                  <tr v-if="pendingTasks.length === 0 && loading === true">
                    <td colspan="4" class="text-center">
                      <div v-if="loading" class="d-block position-relative">
                        <p class="d-flex">
                          <span class="spinner-border mr-2" role="status">
                            <span class="sr-only">Loading...</span>
                          </span>
                          Loading tasks...
                        </p>
                      </div>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div class="row" v-if="pendingTasks.length > 3">
              <div class="col-12 col-sm-5 mx-auto">
                <a :href="route('team-members.report.tasks', ['incomplete'])"
                   class="wfh-btn-color-primary wfh-btn-large btn-block">
                  See All
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <transition name="fade">
      <date-filter-modal
          v-if="showFilterModal"
          @close="showFilterModal = false"
          @selected="dateSelected"
      ></date-filter-modal>
    </transition>
  </div>
</template>

<script>
import FilterIcon from '@icons/FilterIcon';
import TaskService from '../../Task/Task';
import { get as getUserSummary } from '../../../portal/services/ReportService';
import { setUserData } from '../../../repositories/UserDataPersistence';
import minutesToHoursFormatter from '../../../utils/minutesToHoursFormatter';
import { mapState } from 'vuex';

const STATS = [
  {
    name: 'Hours Spent',
    number: null,
    unit: 'In total',
    link: route('team-members.report.tasks'),
  },
  {
    name: 'Completed Tasks',
    number: null,
    unit: 'In total',
    link: route('team-members.report.tasks', ['completed']),
  },
  {
    name: 'Incomplete Tasks',
    number: null,
    unit: 'In total',
    link: route('team-members.report.tasks', ['incomplete']),
  },
];

export default {
  components: { FilterIcon },
  data () {
    return {
      taskService: TaskService,
      showFilterModal: false,
      loading: true,
      startDate: null,
      endDate: null,
      companyId: null,
      completedTasks: [],
      pendingTasks: [],
      totalCost: 0,
      currency: '',
      stats: STATS,
    };
  },

  computed: {
    ...mapState(['currentUser']),
  },

  watch: {
    currentUser () {
      this.fetchTasks();

      setUserData(this.currentUser);
      if (this.currentUser.company) {
        this.companyId = this.currentUser.company.id;
        this.getSummaryData();
      }
    },
  },

  methods: {
    minutesToHoursFormatter,

    viewTaskDetails (link) {
      window.location.href = link;
    },

    async getSummaryData () {
      const res = await getUserSummary({
        start_date: this.startDate,
        end_date: this.endDate,
        company_id: this.companyId,
        user_id: this.currentUser.id,
      });
      const data = res.data;
      this.totalCost = data.payment.internal_cost ?? data.payment.client_cost;
      this.currency = data.payment.currency;

      const totalTimeSpent = this.minutesToHoursFormatter(data.time.minutes_spent_on_tasks);
      this.stats[0].number = totalTimeSpent;
      this.stats[1].number = data.meta.total_completed_tasks;
      this.stats[2].number = data.meta.total_pending_tasks;
      localStorage.setItem('wfh_tts', totalTimeSpent);

      this.loading = false;
    },

    async fetchTasks () {
      const completedTasksResponse = await axios.get('/reports/tasks', {
        params: {
          is_personal: true,
          start_date: this.startDate,
          end_date: this.endDate,
          per_page: 4,
          status: 'completed_at',
          user_id: this.currentUser.id,
          company_id: this.companyId,
        },
      });

      const pendingTasksResponse = await axios.get('/reports/tasks', {
        params: {
          is_personal: true,
          start_date: this.startDate,
          end_date: this.endDate,
          per_page: 4,
          status: 'pending',
          user_id: this.currentUser.id,
          company_id: this.companyId,
        },
      });

      this.completedTasks = this.prepareTasks(completedTasksResponse.data.data);
      this.pendingTasks = this.prepareTasks(pendingTasksResponse.data.data);
    },

    dateSelected (startDate, endDate) {
      this.startDate = startDate;
      this.endDate = endDate;

      this.fetchTasks();
      this.getSummaryData();
      this.showFilterModal = false;
    },

    prepareTasks (tasks) {
      return tasks.map((task) => {
        return {
          date: moment(task.completed_at ?? task.combined_created_at).format('DD MMM YYYY'),
          id_hash: task.id_hash,
          id: task.id,
          title: task.title,
          assignee: task.users,
          time_spent: this.minutesToHoursFormatter(task.total_minutes_spent),
          status: !task.completed && task.total_minutes_spent > 0 ? 'on-going' : task.completed ? 'completed' : 'pending',
          detailsPageUrl: route('team-members.report.task-details', {
            hashedTaskId: task.id_hash,
            start_date: this.startDate,
            end_date: this.endDate,
          }),
        };
      });
    },

    setDefaultDateRange () {
      this.startDate = localStorage.getItem('wfh_tm_sd') ?? moment().startOf('month').format('YYYY-MM-DD');
      this.endDate = localStorage.getItem('wfh_tm_ed') ?? moment().format('YYYY-MM-DD');

      localStorage.setItem('wfh_tm_sd', this.startDate);
      localStorage.setItem('wfh_tm_ed', this.endDate);
    },

    formatDateForDisplay (date) {
      return moment(date).format('DD/MM/YYYY');
    },
  },

  mounted () {
    this.setDefaultDateRange();
  },
};
</script>

<style lang="scss" scoped>
.subsection-title {
  color: #303030;
  font-family: Lato, sans-serif;
  font-size: 20px;
  font-weight: bold;
  letter-spacing: 0;
  line-height: 28px;
}

.subsection-subtitle {
  color: #8392a6;
  font-family: "Open Sans", sans-serif;
  font-size: 14px;
  letter-spacing: 0;
  line-height: 24px;
}

.wfh-table {
  width: 100%;

  thead {
    tr {
      background-color: #eff1f5;
      color: #808fa4;
      font-family: "Open Sans", sans-serif;
      font-size: 12px;
      font-weight: 600;
      letter-spacing: 0.43px;
      line-height: 22px;
    }
  }

  tbody {
    color: #303030;
    font-family: "Open Sans", sans-serif;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0;
    line-height: 22px;
  }

  td {
    padding: 15px;
  }
}
</style>
